import React from "react";
import { cn } from "@/lib/utils";

interface MarkdownRendererProps {
  content?: string | null;
  className?: string;
}

/**
 * MarkdownRenderer: Render an toàn cú pháp Markdown (in đậm, in nghiêng, code, links, bullets, callout)
 * Chống XSS bằng cách escape HTML trước khi render.
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  if (!content || !content.trim()) {
    return null;
  }

  // 1. Helper an toàn escape HTML và format inline markdown
  const formatInline = (text: string) => {
    let formatted = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Markdown Link: [text](url)
    formatted = formatted.replace(
      /\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer" class="text-blue-600 underline font-medium hover:text-blue-800 break-all">$1</a>'
    );

    // Bold: **text** hoặc __text__
    formatted = formatted.replace(
      /\*\*(.*?)\*\*/g,
      '<strong class="font-bold text-slate-900">$1</strong>'
    );
    formatted = formatted.replace(
      /__(.*?)__/g,
      '<strong class="font-bold text-slate-900">$1</strong>'
    );

    // Italic: *text* hoặc _text_
    formatted = formatted.replace(
      /\*(.*?)\*/g,
      '<em class="italic text-slate-700">$1</em>'
    );
    formatted = formatted.replace(
      /_([^_]+)_/g,
      '<em class="italic text-slate-700">$1</em>'
    );

    // Inline Code: `code`
    formatted = formatted.replace(
      /`([^`]+)`/g,
      '<code class="px-1.5 py-0.5 rounded text-[11px] font-mono bg-slate-100 text-emerald-800 border border-slate-200">$1</code>'
    );

    // Standalone URLs (không nằm trong thẻ a href)
    formatted = formatted.replace(
      /(?<!href=")(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noreferrer" class="text-blue-600 underline font-medium hover:text-blue-800 break-all">$1</a>'
    );

    return formatted;
  };

  // 2. Render từng dòng có cấu trúc
  const lines = content.split("\n");

  return (
    <div className={cn("text-xs sm:text-[13px] leading-relaxed text-slate-700 space-y-1", className)}>
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={idx} className="h-1.5" />;
        }

        // Callout dòng chỉ dẫn: 👉 ...
        if (trimmed.startsWith("👉")) {
          const calloutText = trimmed.replace(/^👉\s*/, "");
          return (
            <div
              key={idx}
              className="my-1.5 p-2.5 rounded-lg bg-emerald-50/90 border border-emerald-200 text-xs sm:text-[13px] font-medium text-emerald-950 flex items-start gap-2 shadow-2xs"
            >
              <span className="shrink-0 text-base leading-none mt-0.5">👉</span>
              <span
                className="flex-1 min-w-0 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatInline(calloutText) }}
              />
            </div>
          );
        }

        // Heading 3: ### ...
        if (trimmed.startsWith("### ")) {
          return (
            <h4
              key={idx}
              className="text-xs sm:text-sm font-bold text-slate-900 mt-2 mb-1 pb-1 border-b border-slate-200"
              dangerouslySetInnerHTML={{ __html: formatInline(trimmed.replace("### ", "")) }}
            />
          );
        }

        // Heading 4: #### ...
        if (trimmed.startsWith("#### ")) {
          return (
            <h5
              key={idx}
              className="text-[12px] sm:text-xs font-bold text-slate-800 mt-1.5 mb-0.5 flex items-center gap-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: formatInline(trimmed.replace("#### ", "")) }} />
            </h5>
          );
        }

        // Bullet point: - hoặc *
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={idx} className="flex items-start gap-2 my-0.5 text-slate-700 leading-relaxed pl-2">
              <span className="text-emerald-500 font-bold shrink-0 mt-0.5">•</span>
              <span
                className="flex-1 min-w-0"
                dangerouslySetInnerHTML={{ __html: formatInline(trimmed.substring(2)) }}
              />
            </div>
          );
        }

        // Checkbox: [x] hoặc [ ]
        if (trimmed.startsWith("[x] ") || trimmed.startsWith("[ ] ")) {
          const isChecked = trimmed.startsWith("[x] ");
          return (
            <div key={idx} className="flex items-start gap-2 my-0.5 text-slate-700 leading-relaxed pl-2">
              <span className={cn("text-xs shrink-0 mt-0.5 font-bold", isChecked ? "text-emerald-600" : "text-slate-400")}>
                {isChecked ? "☑" : "☐"}
              </span>
              <span
                className="flex-1 min-w-0"
                dangerouslySetInnerHTML={{ __html: formatInline(trimmed.substring(4)) }}
              />
            </div>
          );
        }

        // Dòng bình thường
        return (
          <p
            key={idx}
            className="my-0.5 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }}
          />
        );
      })}
    </div>
  );
}
